import React, { useCallback, useState } from 'react';

import { ApiService, Urls } from 'js/src/client';
import { AuthQueryParams } from 'js/src/components/auth/auth-consts';
import { setLastLoginMethod } from 'js/src/components/auth/common/lastLoginMethod';
import { SOCIAL_BUTTON_PROVIDERS } from 'js/src/components/auth/common/SocialButton';
import { useFormFeedback } from 'js/src/components/auth/common/useFormFeedback';
import { Toast } from 'js/src/components/common/Toast';
import Button, { BUTTON_SIZES, BUTTON_TYPE } from 'js/src/components/common/ui/Button';
import TextInput, {
    AUTOCOMPLETE_VALUES,
    TEXT_INPUT_VARIANTS,
    type TypeInputChangeEvent,
} from 'js/src/components/common/ui/TextInput';
import { isApiError } from 'js/src/types';

type Props = {
    initialEmail?: string;
    inviteId?: string;
};

const SSOVerificationForm = React.memo(function SSOVerificationForm({
    initialEmail = '',
    inviteId = '',
}: Props) {
    const [emailVal, setInitialEmail] = useState(initialEmail);
    const [isValidatingEmail, setIsValidatingEmail] = useState(false);
    const [formFeedback, setFormFeedback] = useFormFeedback({
        email: '',
    });

    const emailOnChangeHandler = useCallback(
        (e: TypeInputChangeEvent) => setInitialEmail(e.currentTarget.value),
        [],
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setFormFeedback({ type: 'CLEAR_ERRORS' });
        setIsValidatingEmail(true);
        ApiService.ssoVerficationCreate({
            body: { email: emailVal.toLowerCase(), invite_uuid: inviteId },
        })
            .then((response) => {
                const idp = response.data.idp;
                if (idp) {
                    setLastLoginMethod(SOCIAL_BUTTON_PROVIDERS.SSO);
                    window.location.href =
                        Urls.SSO_AUTH +
                        `?${AuthQueryParams.NEXT}=` +
                        encodeURIComponent(window.location.pathname + window.location.search) +
                        `&${AuthQueryParams.IDP}=` +
                        idp;
                }
            })
            .catch((error: unknown) => {
                if (isApiError(error) && typeof error.body === 'object' && error.body !== null) {
                    setFormFeedback({
                        type: 'SET_ERROR',
                        payload: error.body,
                    });
                } else {
                    Toast.error('An error occurred. Please try again.');
                }
            })
            .finally(() => setIsValidatingEmail(false));
    };

    const disableLoginButton = isValidatingEmail || !emailVal.trim().length;

    return (
        <form
            id="sso-email-validation-form"
            method="post"
            className="form-sidebar"
            data-testid="sso-email-validation-form"
            onSubmit={handleSubmit}
        >
            <TextInput
                labelText="Email"
                text={emailVal}
                variant={TEXT_INPUT_VARIANTS.STANDARD}
                onChange={emailOnChangeHandler}
                formFeedbackMessage={formFeedback.email || formFeedback.message}
                data-testid="email"
                autocomplete={AUTOCOMPLETE_VALUES.EMAIL}
                ignorePasswordManagers={false}
            />
            <br />
            <div className="my-l5-16">
                <Button
                    type={BUTTON_TYPE.SUBMIT}
                    text={isValidatingEmail ? 'Logging in...' : 'Log in'}
                    className="w-100"
                    size={BUTTON_SIZES.LG}
                    disabled={disableLoginButton}
                />
            </div>
        </form>
    );
});

export default SSOVerificationForm;
